import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronLeft, ChevronRight, ExternalLink, FileText, Inbox, Loader2, RefreshCw, X } from "lucide-react";
import type { ApprovalItem } from "@/lib/api/approvalsApi";
import { ApiError } from "@/lib/api/chatApi";
import { createDefaultHumanResponses, prettifyText, type ActionRequest } from "@/lib/approvals/types";
import { formatMoney, normalizeCurrency, withCurrency } from "@/lib/approvals/money";
import { agentChipClass } from "@/lib/agentColors";

interface ApprovalDetailProps {
  item: ApprovalItem | null;
  onResume: (response: { type: string; args: unknown }) => Promise<unknown>;
  /**
   * Cierra esta tarea y re-encola su documento como run NUEVO (el agente lo vuelve
   * a analizar con el código actual). Opcional: sin ella no se pinta el botón.
   */
  onReprocess?: () => Promise<unknown>;
  onResolved: () => void;
}

/**
 * Claves de los args que son METADATO de presentacion, NO datos editables:
 * - documento_urls: imagenes (paginas) del documento original.
 * - _field_options: opciones por campo para pintar <select> in-place.
 * - motivos: razones de revision; se muestran como chips de solo lectura.
 */
const PRESENTATION_KEYS = new Set(["documento_urls", "documento_descarga", "origen", "_field_options", "motivos", "informe", "_readonly_fields", "_depto_regla"]);

/** Orden preferido de los campos de una linea (resto al final). */
const LINE_FIELD_ORDER = [
  "concepto", "cuenta_gasto", "departamento", "tipo_coste", "area", "vat", "base", "iva",
];
/** Orden preferido de los campos de cabecera (escalares); resto al final.
 * clave_reparto: clave de reparto analítico (X3 DSP). Cuando se elige, el gasto se
 * reparte entre varios departamentos/áreas; vacía = destino único.
 * moneda: divisa del asiento (ISO), EDITABLE (derivada del proveedor en X3; el
 * revisor la corrige si la factura está en otra). Va junto a los importes que
 * califica; su valor VIVO etiqueta todos los importes de la vista.
 * destino: sociedad/planta del grupo donde se graba (FON/001, TER/T01, FON/003,
 * FON/004). Primero: decide todo lo demás (IVA por país). Obligatorio: vacío = el
 * agente no pudo decidirlo y nunca se graba en una sociedad por defecto. */
const SCALAR_FIELD_ORDER = ["destino", "proveedor", "cif", "numero", "fecha", "fecha_contable", "clave_reparto", "moneda", "base", "iva", "tipo_retencion", "retencion", "vat_retencion", "total", "comentario_1", "comentario_2", "comentario_3"];
/** Campos de cabecera obligatorios: vacíos bloquean Aprobar (el backend lo repite). */
const REQUIRED_SCALAR_FIELDS = new Set(["destino"]);
const WIDE_FIELDS = new Set(["concepto", "descripcion", "description", "comentario_1", "comentario_2", "comentario_3"]);
const NUMERIC_FIELDS = new Set(["base", "iva", "tipo_retencion", "retencion", "importe", "total", "cantidad", "precio"]);
/**
 * Campos numéricos que son un PORCENTAJE, no un importe: se muestran con "%" en vez
 * de con la divisa. Sin esto, `tipo_retencion` = 15 se pintaba como "15 EUR" — un
 * dato que se lee mal de un vistazo justo en la pantalla donde se aprueba dinero.
 */
const PERCENT_FIELDS = new Set(["tipo_retencion"]);
/**
 * Campos de LÍNEA obligatorios SIEMPRE para registrar en X3 (lo que el guard del
 * backend exige antes de ZBIS). Vacíos -> error de validación que bloquea Aprobar.
 * `departamento` es obligatorio TAMBIÉN, pero CONDICIONAL: solo si NO hay clave de
 * reparto (ver LINE_REQUIRED_IF_NO_REPARTO) — con clave, la analítica la define ella.
 * `tipo_coste`/`area` no son obligatorios (X3 los deriva).
 */
const LINE_REQUIRED_FIELDS = new Set(["cuenta_gasto", "vat", "base", "iva"]);
/** Campos de línea obligatorios SOLO cuando no hay clave de reparto en la cabecera. */
const LINE_REQUIRED_IF_NO_REPARTO = new Set(["departamento"]);
/** Campos de fecha: se editan con <input type="date"> (no caja de texto). */
const DATE_FIELDS = new Set(["fecha", "fecha_contable", "fecha_operacion", "fecha_vencimiento"]);

/**
 * Convierte un valor de fecha a `yyyy-MM-dd` (lo que exige <input type="date">),
 * o "" si no es interpretable. Acepta ISO ("2025-05-12", con o sin hora) y el
 * formato español "DD/MM/YYYY".
 */
function toDateInputValue(value: string): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const es = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (es) return `${es[3]}-${es[2].padStart(2, "0")}-${es[1].padStart(2, "0")}`;
  return "";
}

/**
 * Formatea una fecha para MOSTRAR (readonly) como DD/MM/YYYY con ceros a la
 * izquierda, consistente. Parte del valor ISO (yyyy-MM-dd) que ya guarda el estado;
 * si no es una fecha ISO, devuelve el valor tal cual.
 */
function formatDateDisplay(isoValue: string): string {
  const m = (isoValue ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : isoValue;
}

/**
 * Clase base de los campos editables (input/select/textarea): foco unificado al
 * rojo Fonestar (var(--ring)) — nada de azul nativo del navegador. Si `error`, el
 * borde y el ring pasan a rojo de error. `extra` para clases adicionales (resize-y).
 */
function fieldClass(error: boolean, extra = ""): string {
  const base = "w-full rounded-lg border bg-[var(--background)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2";
  const state = error
    ? "border-red-500 focus:ring-red-500"
    : "border-[var(--border)] focus:ring-[var(--ring)]";
  return `${base} ${state}${extra ? " " + extra : ""}`;
}

/** Clase de un <select>: estilo normal; solo el error (obligatorio vacío) pinta en
 * rojo. (Antes un campo vacío no-obligatorio se coloreaba en ámbar para "avisar"; se
 * quitó — daba un aspecto feo, sobre todo en modo oscuro.) */
function selectClass(error: boolean): string {
  const base = "w-full rounded-lg border px-3 py-1.5 text-sm focus:outline-none focus:ring-2";
  if (error) return `${base} border-red-500 bg-[var(--background)] focus:ring-red-500`;
  return `${base} border-[var(--border)] bg-[var(--background)] focus:ring-[var(--ring)]`;
}

/**
 * Input de importe con la divisa de la factura (código ISO: EUR/USD...) como adorno
 * gris dentro del campo, a la derecha. La divisa NO entra en el value (no rompe el
 * parseo numérico); solo decora. Sin divisa conocida no se pinta adorno (no
 * asumimos €: la divisa es la de la factura, no siempre EUR).
 */
function MoneyInput({
  value,
  onChange,
  error,
  ariaLabel,
  currency,
}: {
  value: string;
  onChange: (v: string) => void;
  error: boolean;
  ariaLabel: string;
  currency?: string;
}) {
  return (
    <div className="relative sm:max-w-[12rem]">
      <input
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={fieldClass(error, currency ? "pr-12" : undefined)}
      />
      {currency && (
        <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-sm text-[var(--muted-foreground)]">
          {currency}
        </span>
      )}
    </div>
  );
}

/**
 * Parsea un importe tecleado o mostrado a número, o null si no es interpretable.
 * ÚNICO parseo de importes del formulario (validación, cuadre y args del resume),
 * espejo del `_to_float` del backend (registrar_factura_x3) para que lo que el
 * front acepta sea exactamente lo que el guard de X3 aceptará:
 * - ambos separadores: el de más a la derecha es el decimal ("1.234,56" y
 *   "1,234.56" → 1234.56)
 * - solo coma: decimal es-ES ("30,52" → 30.52; varias comas = millares)
 * - solo punto: decimal ("478.8" → 478.8 — antes se comía el punto → 4788)
 */
function parseAmount(raw: string): number | null {
  let s = (raw ?? "").trim();
  if (s === "") return null;
  s = s.replace(/\s/g, "");
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma >= 0 && lastDot >= 0) {
    s = lastComma > lastDot ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  } else if (lastComma >= 0) {
    s = (s.match(/,/g) ?? []).length === 1 ? s.replace(",", ".") : s.replace(/,/g, "");
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Valida un valor editable y devuelve un código de error (o null si es válido).
 * - Numérico ("123,45"/"123.45"): rechaza texto no numérico.
 * - Obligatorio: rechaza vacío.
 * Mismo criterio que el guard del backend (defensa en dos capas).
 */
function validateField(value: string, opts: { numeric: boolean; required: boolean }): string | null {
  const raw = (value ?? "").trim();
  if (opts.required && raw === "") return "obligatorio";
  if (opts.numeric && raw !== "" && parseAmount(raw) === null) return "debe ser un número";
  return null;
}

/**
 * Importes en formato espanol (coma decimal) para MOSTRAR: 478.8 -> "478,80".
 * El usuario revisa cifras contables y espera la coma. Solo aplica a numeros;
 * cualquier otro valor se deja tal cual. `inputToValue` deshace la coma al editar.
 */
function formatNumericForDisplay(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return valueToInput(value);
}

function orderScalarKeys(keys: string[]): string[] {
  const known = SCALAR_FIELD_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !SCALAR_FIELD_ORDER.includes(k));
  return [...known, ...rest];
}

/**
 * Parsea un importe MOSTRADO ("1.234,56" o "1234.56") a numero, o null si no es
 * interpretable. Delegado en `parseAmount` (el parseo único de importes).
 * Lo usa el banner de cuadre (suma de bases de linea vs base de cabecera).
 */
function parseDisplayedNumber(raw: string): number | null {
  if (typeof raw !== "string") return null;
  return parseAmount(raw);
}

function isObjectArray(value: unknown): value is Record<string, unknown>[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((el) => el !== null && typeof el === "object" && !Array.isArray(el))
  );
}

/**
 * Extrae las lineas de la imputacion REALMENTE REGISTRADA desde `item.result`
 * (`result.imputacion.lineas`) — la fuente de verdad de lo que se grabo en X3,
 * frente al payload que solo refleja lo PROPUESTO al humano. Valida tipos con
 * cautela: `result` objeto, `imputacion` objeto, `lineas` array de objetos no
 * vacio. Devuelve null si no hay imputacion registrada (rechazadas, filas
 * antiguas, indeterminado sin lineas) -> la seccion no se pinta.
 */
function extractRegisteredImputationLines(result: Record<string, unknown> | null | undefined): Record<string, unknown>[] | null {
  if (!result || typeof result !== "object") return null;
  const imputacion = (result as Record<string, unknown>).imputacion;
  if (!imputacion || typeof imputacion !== "object" || Array.isArray(imputacion)) return null;
  const lineas = (imputacion as Record<string, unknown>).lineas;
  return isObjectArray(lineas) ? lineas : null;
}

/**
 * `true` si el alta en X3 quedo sin confirmar (`estado: "registrado_indeterminado"`):
 * el asiento pudo no crearse. Se avisa al revisor para que verifique a mano.
 */
function isRegisteredIndeterminate(result: Record<string, unknown> | null | undefined): boolean {
  return !!result && typeof result === "object" && result.estado === "registrado_indeterminado";
}

/**
 * Retencion de IRPF que NO quedo aplicada, o no se pudo confirmar, en X3
 * (`result.retencion_pendiente`). La factura existe, pero sin el codigo su
 * vencimiento va por el BRUTO. Devuelve la clave i18n del aviso segun lo que paso,
 * con el codigo pedido, o `null` si no hay nada pendiente. Cada caso pide una accion
 * distinta: repetir a mano un codigo que X3 rechazo seria una instruccion falsa.
 */
function pendingWithholdingNotice(
  result: Record<string, unknown> | null | undefined,
): { key: string; codigo: string } | null {
  if (!result || typeof result !== "object") return null;
  const pendiente = result.retencion_pendiente;
  if (!pendiente || typeof pendiente !== "object" || Array.isArray(pendiente)) return null;
  const p = pendiente as Record<string, unknown>;
  const codigo = typeof p.codigo === "string" ? p.codigo : "";
  const motivo = p.motivo;
  const paso = p.paso;
  let key = "approvals.pendingWithholding";
  if (motivo === "retencion_sin_codigo") key = "approvals.pendingWithholdingNoCode";
  else if (motivo === "retencion_no_verificada")
    key = codigo ? "approvals.pendingWithholdingUnverified" : "approvals.pendingWithholdingUnverifiedNoCode";
  else if (motivo === "retencion_incompleta" && paso === "tax1") key = "approvals.pendingWithholdingRejected";
  else if (motivo === "retencion_incompleta" && paso === "comprobacion")
    key = "approvals.pendingWithholdingMismatch";
  return { key, codigo };
}

function extractDocumentUrls(args: Record<string, unknown> | undefined): string[] {
  const raw = args?.documento_urls;
  if (!Array.isArray(raw)) return [];
  return raw.filter((u): u is string => typeof u === "string" && u.length > 0);
}

/** Opcion de un <select>: value = CODIGO X3 (lo que se graba), label = texto.
 * `pais` (ES/PT/FR) solo lo traen las opciones de `vat` y `destino`: con el se
 * filtra el desplegable de IVA por el pais del destino (ver ivaPorDestino). */
interface FieldOption {
  value: string;
  label: string;
  pais?: string;
}

/**
 * Opciones de `vat` para el destino elegido: las de su pais mas las que no traen
 * `pais`, que nunca se ocultan. Los codigos de REGIMEN (SP030/SP021), validos en
 * cualquier destino, los manda el backend SIN `pais` a proposito
 * (factura_compra._opt_iva): la lista vive solo alli. El backend
 * (codigo_iva_coherente) rechaza al grabar un codigo de otro pais; aqui solo se
 * evita ofrecerlo. `null` = sin filtro (destino vacio o desconocido, o una
 * aprobacion anterior cuyas opciones de IVA no traen `pais`): se ofrecen TODAS y
 * cambiar de destino no vacia nada.
 */
function ivaPorDestino(
  vat: FieldOption[] | undefined,
  destinos: FieldOption[] | undefined,
  destino: string | undefined,
): FieldOption[] | null {
  const pais = destinos?.find((d) => d.value === (destino ?? "").trim())?.pais;
  if (!pais || !vat?.some((o) => o.pais)) return null;
  return vat.filter((o) => !o.pais || o.pais === pais);
}

function extractFieldOptions(args: Record<string, unknown> | undefined): Record<string, FieldOption[]> {
  const raw = args?._field_options;
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, FieldOption[]> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(v)) {
      // Cada opcion es {value, label}. Toleramos string suelto (legado) -> value=label.
      const opts = v
        .map((o): FieldOption | null => {
          if (typeof o === "string") return o ? { value: o, label: o } : null;
          if (o && typeof o === "object" && typeof (o as FieldOption).value === "string") {
            const fo = o as FieldOption;
            const opt: FieldOption = { value: fo.value, label: fo.label || fo.value };
            if (typeof fo.pais === "string" && fo.pais) opt.pais = fo.pais;
            return opt;
          }
          return null;
        })
        .filter((o): o is FieldOption => o !== null);
      if (opts.length) out[k] = opts;
    }
  }
  return out;
}

function extractMotivos(args: Record<string, unknown> | undefined): string[] {
  const raw = args?.motivos;
  if (!Array.isArray(raw)) return [];
  return raw.map((m) => (typeof m === "string" ? m : String(m))).filter(Boolean);
}

/**
 * Campos marcados como solo-lectura por el agente (_readonly_fields): se muestran
 * para contraste pero sin input editable. Generico: cualquier agente puede mandarlo.
 */
function extractReadonlyFields(args: Record<string, unknown> | undefined): Set<string> {
  const raw = args?._readonly_fields;
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.filter((f): f is string => typeof f === "string" && f.length > 0));
}

/**
 * Regla de negocio depto -> (tipo_coste, area) que envia el backend en `_depto_regla`
 * (FUENTE DE VERDAD: el backend; aqui solo un ESPEJO para recalcular en vivo al editar
 * el depto y pintar esos dos campos readonly). Formato del backend: {depto: [tc, area]}.
 * Deptos ausentes (15/99) = libres (el revisor elige). Devuelve un Map normalizado.
 */
function extractDeptoRegla(args: Record<string, unknown> | undefined): Map<string, [string, string]> {
  const raw = args?._depto_regla;
  const out = new Map<string, [string, string]>();
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(v) && v.length === 2 && typeof v[0] === "string" && typeof v[1] === "string") {
      out.set(k, [v[0], v[1]]);
    }
  }
  return out;
}

/** Normaliza un codigo de depto para buscar en la regla: zero-pad a 2 (igual que el backend). */
function normDepto(value: string): string {
  const s = (value ?? "").trim();
  return /^\d$/.test(s) ? s.padStart(2, "0") : s;
}

function valueToInput(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function inputToValue(raw: string, original: unknown, numeric = false): unknown {
  // Campo de importe (NUMERIC_FIELDS) o valor que ya era numérico: parsear SIEMPRE
  // a número. `numeric` es imprescindible porque el original puede venir null de la
  // propuesta (caso real 20266012301: base de línea null, el revisor tecleó "30,52"
  // y viajó como TEXTO al backend, que la rechazó una y otra vez con "base no
  // válida"). Si no parsea, se manda el texto tal cual y el guard lo explica.
  if (numeric || typeof original === "number") {
    const n = parseAmount(raw);
    return raw.trim() !== "" && n !== null ? n : raw;
  }
  if (original !== null && typeof original === "object") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

function orderLineKeys(keys: string[]): string[] {
  const known = LINE_FIELD_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !LINE_FIELD_ORDER.includes(k));
  return [...known, ...rest];
}

function unionKeys(rows: Record<string, unknown>[]): string[] {
  const keys: string[] = [];
  for (const row of rows) for (const k of Object.keys(row)) if (!keys.includes(k)) keys.push(k);
  return keys;
}

/**
 * Controles de navegacion de paginas (anterior / contador / siguiente). Compartido
 * por el panel lateral y el modal grande, asi ambos navegan las paginas del
 * documento. Se oculta si hay una sola pagina.
 */
function PageNav({
  page,
  total,
  onPrev,
  onNext,
}: {
  page: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  if (total <= 1) return null;
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        aria-label={t("approvals.documentPrev")}
        onClick={onPrev}
        disabled={page === 0}
        className="rounded-full border border-[var(--border)] bg-[var(--background)] p-1 disabled:opacity-40"
      >
        <ChevronLeft size={14} />
      </button>
      <span className="min-w-[3.5rem] text-center text-xs font-medium text-[var(--muted-foreground)]">
        {page + 1} / {total}
      </span>
      <button
        type="button"
        aria-label={t("approvals.documentNext")}
        onClick={onNext}
        disabled={page === total - 1}
        className="rounded-full border border-[var(--border)] bg-[var(--background)] p-1 disabled:opacity-40"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

/**
 * Visor grande del documento (modal), con navegacion de paginas. Cierra con la X,
 * click en el overlay y Escape; las flechas del teclado pasan de pagina.
 */
function DocumentModal({
  urls,
  page,
  setPage,
  onClose,
}: {
  urls: string[];
  page: number;
  setPage: (updater: (p: number) => number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setPage((p) => Math.max(0, p - 1));
      else if (e.key === "ArrowRight") setPage((p) => Math.min(urls.length - 1, p + 1));
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const prevFocus = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus();
    };
  }, [onClose, setPage, urls.length]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("approvals.documentTitle")}
      className="fixed inset-0 z-50 flex flex-col bg-black/85 p-4 sm:p-8"
      onClick={onClose}
    >
      <div className="mb-3 flex items-center justify-center gap-4" onClick={(e) => e.stopPropagation()}>
        <PageNav
          page={page}
          total={urls.length}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(urls.length - 1, p + 1))}
        />
        <button
          ref={closeBtnRef}
          type="button"
          aria-label={t("approvals.action.cancel")}
          onClick={onClose}
          className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <X size={20} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto" onClick={(e) => e.stopPropagation()}>
        <img
          src={urls[page]}
          alt={t("approvals.documentPage", { number: page + 1 })}
          className="mx-auto w-auto max-w-full rounded-md bg-white shadow-lg"
        />
      </div>
    </div>
  );
}

/**
 * Redimensionado del panel del documento (split horizontal, lg+): devuelve el ancho
 * del panel en % (`docWidthPct`), el ref del contenedor del split (`splitRef`) y el
 * handler `onDividerPointerDown` para el divisor arrastrable. La preferencia se
 * PERSISTE en localStorage (clave compartida por las vistas pending y resuelta, para
 * que el ancho elegido sea coherente entre ambas). Acotado 25–60% para que ninguna
 * columna desaparezca. Extraido para reutilizarse en ApprovalDetail (pending) y
 * ResolvedDetail sin duplicar la logica de arrastre.
 */
function useDocPanelResize() {
  const [docWidthPct, setDocWidthPct] = useState<number>(() => {
    const saved = Number(localStorage.getItem("approvalsDocWidthPct"));
    return Number.isFinite(saved) && saved >= 25 && saved <= 60 ? saved : 40;
  });
  const splitRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current || !splitRef.current) return;
      const r = splitRef.current.getBoundingClientRect();
      const pct = ((r.right - e.clientX) / r.width) * 100;
      setDocWidthPct(Math.min(60, Math.max(25, pct)));
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setDocWidthPct((pct) => {
        try {
          localStorage.setItem("approvalsDocWidthPct", String(Math.round(pct)));
        } catch { /* localStorage no disponible: la preferencia no persiste, sin romper */ }
        return pct;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const onDividerPointerDown = (e: ReactPointerEvent) => {
    draggingRef.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    e.preventDefault();
  };

  return { docWidthPct, splitRef, onDividerPointerDown };
}

/**
 * Divisor vertical arrastrable entre la columna de datos y la del documento (lg+).
 * Compartido por las vistas pending y resuelta. `onPointerDown` viene de
 * useDocPanelResize.
 */
function DocumentDivider({ onPointerDown }: { onPointerDown: (e: ReactPointerEvent) => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("approvals.resizeDocument")}
      onPointerDown={onPointerDown}
      className="hidden shrink-0 cursor-col-resize items-stretch px-1.5 lg:flex"
    >
      <div className="w-px flex-1 rounded bg-[var(--border)] transition-colors hover:bg-[var(--ring)]" />
    </div>
  );
}

/**
 * Panel lateral del documento (split view): UNA pagina a la vez con navegacion,
 * para contrastar el documento original con los datos extraidos SIN perder de
 * vista los campos. Cabecera con navegacion, "Descargar original" y cerrar el panel.
 * La imagen es clicable para ampliar (modal) — reemplaza al antiguo boton "Ampliar".
 * `downloadUrl`: URL del ARCHIVO ORIGINAL descargable (el PDF/fichero que llego),
 * distinto de `urls` (imagenes PNG por pagina). Si no hay original, el enlace cae
 * a la imagen de la pagina actual (fallback) con etiqueta "Abrir en pestana".
 */
function DocumentPanel({
  urls,
  page,
  setPage,
  onZoom,
  onClose,
  downloadUrl,
}: {
  urls: string[];
  page: number;
  setPage: (updater: (p: number) => number) => void;
  onZoom: () => void;
  onClose: () => void;
  downloadUrl?: string | null;
}) {
  const { t } = useTranslation();
  // Enlace al ARCHIVO ORIGINAL si lo hay; si no, fallback a la imagen de la pagina.
  const hasOriginal = typeof downloadUrl === "string" && downloadUrl.length > 0;
  const externalHref = hasOriginal ? (downloadUrl as string) : urls[page];
  return (
    <aside className="flex h-full min-h-0 w-full flex-col rounded-lg border border-[var(--border)] bg-[var(--muted)]">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--border)] px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
          {t("approvals.documentTitle")}
        </span>
        <PageNav
          page={page}
          total={urls.length}
          onPrev={() => setPage((p) => Math.max(0, p - 1))}
          onNext={() => setPage((p) => Math.min(urls.length - 1, p + 1))}
        />
        <div className="ml-auto flex items-center gap-1.5">
          {/* Un unico boton: descargar/abrir el ARCHIVO ORIGINAL en nueva pestana
              (fallback a la imagen de la pagina si no hay original). */}
          <a
            href={externalHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--background)] px-2.5 py-1 text-xs font-medium hover:opacity-90"
          >
            <ExternalLink size={12} />
            {hasOriginal ? t("approvals.downloadDocument") : t("approvals.documentNewTab")}
          </a>
          <button
            type="button"
            aria-label={t("approvals.documentHide")}
            onClick={onClose}
            className="rounded-full p-1 text-[var(--muted-foreground)] hover:bg-[var(--background)]"
          >
            <X size={16} />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {/* La imagen abre el modal de zoom al hacer click (reemplaza a "Ampliar"). */}
        <button
          type="button"
          onClick={onZoom}
          aria-label={t("approvals.documentZoom")}
          className="block w-full cursor-zoom-in"
        >
          <img
            src={urls[page]}
            alt={t("approvals.documentPage", { number: page + 1 })}
            loading="lazy"
            className="w-full rounded border border-[var(--border)] bg-white shadow-sm"
          />
        </button>
      </div>
    </aside>
  );
}

/**
 * Formatea un valor escalar de los args para MOSTRAR en la vista de resultado
 * (solo lectura). Fechas -> DD/MM/YYYY; numeros -> formato es-ES con la divisa de
 * la FACTURA (código ISO; sin divisa conocida, solo el número — no asumimos €);
 * resto -> texto. Objetos/arrays sueltos se serializan como JSON.
 */
function displayScalar(key: string, value: unknown, currency = ""): string {
  if (DATE_FIELDS.has(key)) {
    const iso = toDateInputValue(valueToInput(value));
    return iso ? formatDateDisplay(iso) : (valueToInput(value) || "—");
  }
  if (NUMERIC_FIELDS.has(key)) {
    const disp = formatNumericForDisplay(value);
    return disp ? withCurrency(disp, currency) : "—";
  }
  return valueToInput(value) || "—";
}

/**
 * Vista de RESULTADO (solo lectura) para aprobaciones ya resueltas. NO acciona
 * (no recibe onResume/onResolved): muestra el desenlace, la referencia generada,
 * los metadatos de resolucion, los datos que se registraron y el documento
 * original. Reutiliza los tokens de tema y los paneles de documento del flujo
 * pending (DocumentPanel/DocumentModal), sin ninguna maquinaria de edicion.
 */
function ResolvedDetail({ item }: { item: ApprovalItem }) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [docPanelOpen, setDocPanelOpen] = useState(true);
  // Panel del documento redimensionable (mismo hook que el flujo pending): el revisor
  // puede arrastrar el divisor para ensanchar el documento también en las resueltas.
  const { docWidthPct, splitRef, onDividerPointerDown } = useDocPanelResize();

  // Memoizado para que la identidad sea estable entre renders: el `?? {}` creaba un
  // objeto nuevo cada vez, lo que impedia al React Compiler preservar la memoizacion
  // de los useMemo que dependen de `args` (ver el de scalarKeys/arrays mas abajo).
  const args = useMemo(() => item.payload.action_request.args ?? {}, [item]);
  const documentUrls = extractDocumentUrls(args);
  const hasDoc = documentUrls.length > 0;
  // Divisa de los importes (código ISO): manda la POSTEADA en X3 (result.moneda —
  // el revisor pudo corregirla al aprobar) sobre la del payload original. Vacía
  // (aprobaciones antiguas) -> importes sin sufijo, no asumimos €.
  const moneda =
    normalizeCurrency(item.result?.moneda) || normalizeCurrency(args.moneda);
  // URL del ARCHIVO ORIGINAL descargable (el PDF que llegó), si viene en el payload.
  const downloadUrl =
    typeof args.documento_descarga === "string" && args.documento_descarga
      ? args.documento_descarga
      : null;

  // Contexto (mismo que el flujo pending): resumen del agente (informe) y ORIGEN del
  // documento (de dónde vino: email, GesDoc…). Son metadatos de presentación (van en
  // PRESENTATION_KEYS, no como campos), así que hay que mostrarlos aparte — si no, se
  // pierden en la vista resuelta.
  const informe = typeof args.informe === "string" ? args.informe.trim() : "";
  const origen =
    typeof args.origen === "string" && args.origen.trim() ? args.origen.trim() : null;

  // Imputacion REALMENTE REGISTRADA (result.imputacion.lineas): fuente de verdad de
  // lo grabado en X3. Cuando existe, la mostramos DESTACADA y suprimimos las lineas
  // del payload (que solo eran lo PROPUESTO) para no confundir con dos bloques de
  // lineas — dejamos los escalares de cabecera del payload como identificacion.
  const registeredImputation = useMemo(
    () => extractRegisteredImputationLines(item.result),
    [item],
  );
  const indeterminate = useMemo(() => isRegisteredIndeterminate(item.result), [item]);
  const pendingWithholding = useMemo(() => pendingWithholdingNotice(item.result), [item]);
  const imputationCols = useMemo(
    () => (registeredImputation ? orderLineKeys(unionKeys(registeredImputation)) : []),
    [registeredImputation],
  );

  // Particion de los datos registrados en escalares y arrays de objetos (lineas),
  // saltando los metadatos de presentacion. Mismo criterio que el flujo pending.
  // Si hay imputacion registrada, OMITIMOS los arrays del payload (lineas propuestas)
  // para no duplicar bloques de lineas; solo quedan los escalares de cabecera.
  const { scalarKeys, arrays } = useMemo(() => {
    const sKeys: string[] = [];
    const arr: { key: string; cols: string[]; rows: Record<string, unknown>[] }[] = [];
    for (const [k, v] of Object.entries(args)) {
      if (PRESENTATION_KEYS.has(k)) continue;
      if (isObjectArray(v)) {
        if (registeredImputation) continue; // suprimir lineas del payload cuando hay imputacion real
        const rowsV = v as Record<string, unknown>[];
        arr.push({ key: k, cols: orderLineKeys(unionKeys(rowsV)), rows: rowsV });
      } else {
        sKeys.push(k);
      }
    }
    return { scalarKeys: orderScalarKeys(sKeys), arrays: arr };
    // Depende de `args` (lo que realmente se recorre), no de `item`: `args` ya se
    // deriva de item.payload, y declararlo explicitamente permite al React Compiler
    // preservar la memoizacion. Antes habia un eslint-disable de exhaustive-deps que
    // tapaba precisamente esto.
  }, [args, registeredImputation]);

  const outcome = item.outcome ?? null;
  const outcomeClass =
    outcome === "approved"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
      : outcome === "rejected"
        ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
        : outcome === "reprocessed"
          ? "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300"
          : "bg-[var(--muted)] text-[var(--muted-foreground)]";
  const outcomeLabel =
    outcome === "approved"
      ? t("approvals.outcome.approved")
      : outcome === "rejected"
        ? t("approvals.outcome.rejected")
        : outcome === "reprocessed"
          ? t("approvals.outcome.reprocessed")
          : t("approvals.outcome.resolved");

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden p-4 sm:p-5">
      {/* Encabezado: titulo NEUTRO (no imperativo — ya está resuelta) + chip de agente
          + badge de desenlace. El nombre de la acción (`aprobar_imputacion`) es un verbo
          imperativo que solo tiene sentido en pendientes; aquí el badge y el chip ya
          dicen qué era y cómo acabó. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{t("approvals.resolvedTitle")}</h2>
        <span className={`rounded-full px-2 py-0.5 text-xs ${agentChipClass(item.assistant_id)}`}>
          {t(`agents.${item.assistant_id}.label`, item.assistant_id)}
        </span>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${outcomeClass}`}>
          {outcomeLabel}
        </span>
        {hasDoc && !docPanelOpen && (
          <button
            type="button"
            onClick={() => setDocPanelOpen(true)}
            className="ml-auto flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-xs font-medium hover:opacity-90"
          >
            <FileText size={14} />
            {t("approvals.viewDocument")}
          </button>
        )}
      </div>

      {/* Cuerpo: split resumen | documento (en lg), apilado en movil. El gap horizontal
          en lg lo aporta el divisor (px), como en el flujo pending -> lg:gap-0. */}
      <div ref={splitRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden lg:flex-row lg:gap-0">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-1 pt-1 lg:min-w-[22rem] lg:pr-3">
          {/* Tarjeta de contexto (mismo patrón que el flujo pending): resumen del
              agente (informe) + ORIGEN del documento. El origen (de dónde vino: email,
              GesDoc…) se muestra SIEMPRE que venga; sitúa al revisor. */}
          {(informe || origen) && (
            <div className="mb-4 shrink-0 overflow-hidden rounded-lg border-l-4 border-[var(--primary)] bg-[var(--muted)]">
              {informe && (
                <div className="px-4 py-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                    {t("approvals.report")}
                  </p>
                  <p className="whitespace-pre-line text-sm text-[var(--foreground)]">{informe}</p>
                </div>
              )}
              {origen && (
                <div
                  className={`flex items-start gap-2 px-4 py-2.5 text-xs text-[var(--muted-foreground)]${
                    informe ? " border-t border-[var(--border)]" : ""
                  }`}
                >
                  <Inbox size={14} className="mt-0.5 shrink-0" />
                  <span className="whitespace-pre-line">
                    <span className="font-medium">{t("approvals.origin")}: </span>
                    <span className="text-[var(--foreground)]">{origen}</span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Referencia generada (numero_asiento X3, etc.): destacada en mono. */}
          {item.result_ref && (
            <div className="mb-4 shrink-0 rounded-lg border border-[var(--border)] bg-[var(--muted)] px-4 py-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                {t("approvals.resultRef")}
              </p>
              <p className="font-mono text-lg font-semibold text-[var(--foreground)]">{item.result_ref}</p>
            </div>
          )}

          {/* Aviso: alta en X3 sin confirmar (registrado_indeterminado). El asiento
              pudo no crearse; el revisor debe verificarlo a mano. */}
          {indeterminate && (
            <div
              role="alert"
              className="mb-4 shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
            >
              {t("approvals.registeredIndeterminate")}
            </div>
          )}

          {/* Aviso: la factura se grabo pero la retencion de IRPF no quedo aplicada.
              Sin el codigo en X3 el vencimiento sale por el bruto y se pagaria de mas. */}
          {pendingWithholding !== null && (
            <div
              role="alert"
              className="mb-4 shrink-0 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
            >
              {t(pendingWithholding.key, { codigo: pendingWithholding.codigo })}
            </div>
          )}

          {/* Imputacion REGISTRADA (result.imputacion.lineas): la fuente de verdad de
              lo grabado en X3. Destacada (borde de acento) y con el MISMO estilo de
              tarjetas que las lineas del payload. Solo lectura. */}
          {registeredImputation && (
            <div className="mb-4 shrink-0 rounded-lg border-l-4 border-[var(--primary)] bg-[var(--muted)] p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                {t("approvals.registeredImputation")}
              </p>
              <div className="space-y-3">
                {registeredImputation.map((row, rowIdx) => (
                  <div key={rowIdx} className="rounded-lg border border-[var(--border)] bg-[var(--background)] p-3">
                    {registeredImputation.length > 1 && (
                      <div className="mb-2 text-xs font-semibold text-[var(--muted-foreground)]">
                        {t("approvals.lineNumber", { number: rowIdx + 1 })}
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {imputationCols.map((col) => (
                        <div key={col} className={WIDE_FIELDS.has(col) ? "sm:col-span-2" : undefined}>
                          <p className="text-xs font-medium text-[var(--muted-foreground)]">{prettifyText(col)}</p>
                          <p className="whitespace-pre-line text-sm text-[var(--foreground)]">
                            {displayScalar(col, row[col], moneda)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadatos de resolucion. */}
          <dl className="mb-4 shrink-0 grid grid-cols-1 gap-y-1.5 text-sm sm:grid-cols-2 sm:gap-x-6">
            {item.resolved_at && (
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                  {t("approvals.resolvedAt")}
                </dt>
                <dd className="text-[var(--foreground)]">{new Date(item.resolved_at).toLocaleString()}</dd>
              </div>
            )}
            {item.resolved_by && (
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                  {t("approvals.resolvedBy")}
                </dt>
                <dd className="text-[var(--foreground)]">{item.resolved_by}</dd>
              </div>
            )}
            {item.created_at && (
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                  {t("approvals.created")}
                </dt>
                <dd className="text-[var(--foreground)]">{new Date(item.created_at).toLocaleString()}</dd>
              </div>
            )}
          </dl>

          {/* Datos registrados (solo lectura): escalares como pares label/valor,
              lineas como tarjetas. Sin inputs. */}
          {(scalarKeys.length > 0 || arrays.length > 0) && (
            <div className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                {t("approvals.registeredData")}
              </p>
              {scalarKeys.length > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {scalarKeys.map((k) => (
                    <div key={k} className={WIDE_FIELDS.has(k) ? "sm:col-span-2" : undefined}>
                      <p className="mb-0.5 text-xs font-medium text-[var(--muted-foreground)]">{prettifyText(k)}</p>
                      <p className="whitespace-pre-line text-sm text-[var(--foreground)]">
                        {displayScalar(k, args[k], moneda)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {arrays.map(({ key, cols, rows: rowsV }) => (
                <div key={key}>
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                    {prettifyText(key)}
                  </p>
                  <div className="space-y-3">
                    {rowsV.map((row, rowIdx) => (
                      <div key={rowIdx} className="rounded-lg border border-[var(--border)] bg-[var(--muted)] p-3">
                        {rowsV.length > 1 && (
                          <div className="mb-2 text-xs font-semibold text-[var(--muted-foreground)]">
                            {t("approvals.lineNumber", { number: rowIdx + 1 })}
                          </div>
                        )}
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {cols.map((col) => (
                            <div key={col} className={WIDE_FIELDS.has(col) ? "sm:col-span-2" : undefined}>
                              <p className="text-xs font-medium text-[var(--muted-foreground)]">{prettifyText(col)}</p>
                              <p className="whitespace-pre-line text-sm text-[var(--foreground)]">
                                {displayScalar(col, row[col], moneda)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Divisor arrastrable entre datos y documento (lg+): permite redimensionar el
            panel también en la vista resuelta, igual que en pendientes. */}
        {hasDoc && docPanelOpen && <DocumentDivider onPointerDown={onDividerPointerDown} />}

        {/* Columna del DOCUMENTO (split): ancho controlado por el divisor (lg), ancho
            completo apilado en movil. Reutiliza el panel del flujo pending. */}
        {hasDoc && docPanelOpen && (
          <div
            className="min-h-0 w-full min-w-0 shrink-0 lg:w-[var(--doc-w)]"
            style={{ ["--doc-w" as string]: `${docWidthPct}%` }}
          >
            <DocumentPanel
              urls={documentUrls}
              page={page}
              setPage={setPage}
              onZoom={() => setModalOpen(true)}
              onClose={() => setDocPanelOpen(false)}
              downloadUrl={downloadUrl}
            />
          </div>
        )}
      </div>

      {modalOpen && hasDoc && (
        <DocumentModal urls={documentUrls} page={page} setPage={setPage} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}

export default function ApprovalDetail({ item, onResume, onReprocess, onResolved }: ApprovalDetailProps) {
  const { t } = useTranslation();

  // Estado de edicion in-place (siempre editable, sin modo "Editar").
  const [scalars, setScalars] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<Record<string, string[][]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Primer clic en "Reprocesar" -> arma la confirmación; el segundo ejecuta.
  const [reprocessArmed, setReprocessArmed] = useState(false);
  // `dirty` = el revisor ha tocado ALGUN campo en esta sesion. Fuerza que "Aprobar"
  // mande un EDIT con los valores actuales (no un accept), aunque tras editar y
  // corregir un valor coincida de nuevo con el original. Sin esto, un edit fallido
  // que dejo `propuesta_imputacion` pisado en el backend + una correccion de vuelta
  // al original -> handleApprove detectaria "sin cambios" -> accept -> el backend
  // registraria el state corrupto en vez del valor MOSTRADO. (Bug reproducido en
  // pruebas E2E con factura Acens: UI mostraba 1899,64 pero se registraba 2000,64.)
  const [dirty, setDirty] = useState(false);
  // docPanelOpen = panel lateral (split). modalOpen = visor grande. page = pagina
  // actual, COMPARTIDA por panel y modal (navegar en uno mueve al otro).
  const [docPanelOpen, setDocPanelOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  // Redimensionado del panel del documento (ancho %, ref del split y handler del
  // divisor); preferencia persistida en localStorage. Ver useDocPanelResize.
  const { docWidthPct, splitRef, onDividerPointerDown } = useDocPanelResize();

  // Particion de los args en escalares (input) y arrays de objetos (tarjetas).
  const parsed = useMemo(() => {
    const args = item?.payload.action_request.args ?? {};
    const scalarKeys: string[] = [];
    const arrayKeys: string[] = [];
    const rowKeysByArg: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(args)) {
      if (PRESENTATION_KEYS.has(k)) continue;
      if (isObjectArray(v)) {
        arrayKeys.push(k);
        rowKeysByArg[k] = orderLineKeys(unionKeys(v as Record<string, unknown>[]));
      } else {
        scalarKeys.push(k);
      }
    }
    return { scalarKeys: orderScalarKeys(scalarKeys), arrayKeys, rowKeysByArg };
  }, [item]);

  const documentUrls = useMemo(
    () => extractDocumentUrls(item?.payload.action_request.args),
    [item],
  );
  const fieldOptions = useMemo(
    () => extractFieldOptions(item?.payload.action_request.args),
    [item],
  );
  const motivos = useMemo(
    () => extractMotivos(item?.payload.action_request.args),
    [item],
  );
  // Informe redactado (gpt-4.1-nano) en lenguaje de finanzas: se muestra arriba del
  // formulario. Los `motivos` crudos quedan como detalle tecnico debajo.
  const informe = useMemo(() => {
    const v = item?.payload.action_request.args?.informe;
    return typeof v === "string" ? v.trim() : "";
  }, [item]);
  const readonlyFields = useMemo(
    () => extractReadonlyFields(item?.payload.action_request.args),
    [item],
  );
  // Regla depto -> (tipo_coste, area): espejo del backend para recalcular en vivo.
  const deptoRegla = useMemo(
    () => extractDeptoRegla(item?.payload.action_request.args),
    [item],
  );

  // Inicializa el estado editable SOLO al cambiar de aprobacion seleccionada
  // (correlation_id), NO en cada objeto nuevo que trae el poll. Si dependieramos del
  // `item` por referencia, cada poll (cada 5s) reinicializaria el formulario y
  // perderia lo que el revisor esta editando. El cuadre se calcula sobre `scalars`/`rows`.
  const initializedIdRef = useRef<string | null>(null);
  const currentId = item?.correlation_id ?? null;
  useEffect(() => {
    if (!item) {
      initializedIdRef.current = null;
      return;
    }
    if (initializedIdRef.current === item.correlation_id) return;  // misma aprobacion -> no resetear
    initializedIdRef.current = item.correlation_id;
    const args = item.payload.action_request.args;
    const initScalars: Record<string, string> = {};
    for (const k of parsed.scalarKeys) {
      if (DATE_FIELDS.has(k)) initScalars[k] = toDateInputValue(valueToInput(args[k]));
      else if (NUMERIC_FIELDS.has(k)) initScalars[k] = formatNumericForDisplay(args[k]);
      else initScalars[k] = valueToInput(args[k]);
    }
    const initRows: Record<string, string[][]> = {};
    for (const k of parsed.arrayKeys) {
      const cols = parsed.rowKeysByArg[k];
      initRows[k] = (args[k] as Record<string, unknown>[]).map((row) =>
        cols.map((c) => (NUMERIC_FIELDS.has(c) ? formatNumericForDisplay(row[c]) : valueToInput(row[c]))),
      );
    }
    setScalars(initScalars);
    setRows(initRows);
    setDirty(false);
    setError(null);
    setBusy(false);
    setReprocessArmed(false);
    setPage(0);
    setDocPanelOpen(true);
    setModalOpen(false);
    // Solo reinicializa al cambiar de aprobacion (currentId). El guard del ref evita
    // resetear el formulario cuando el poll trae el mismo item con otra referencia.
  }, [currentId, item, parsed]);

  if (!item) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-[var(--muted-foreground)]">
        {t("approvals.selectPrompt")}
      </div>
    );
  }

  // Aprobacion ya resuelta (o cualquier estado no accionable): vista de RESULTADO
  // en solo lectura. NO renderizamos el formulario editable ni los botones
  // Aprobar/Rechazar (ya no accionan nada — seria enganoso).
  if (item.status !== "pending" && item.status !== "processing") {
    return <ResolvedDetail item={item} />;
  }

  const interrupt = item.payload;
  const { config, action_request } = interrupt;
  const { hasAccept } = createDefaultHumanResponses(interrupt);
  const hasDoc = documentUrls.length > 0;

  // Modo SOLO-RECHAZAR: cuando el agente desactiva aprobar Y editar (p.ej. extraccion
  // fallida — el documento no se pudo leer), no hay datos que revisar. NO renderizamos
  // los campos vacios, los badges de motivos ni el aviso de validacion: solo el resumen
  // del agente, un enlace de DESCARGA del original (ya que no hay preview) y Rechazar.
  const soloRechazar = config?.allow_accept === false && config?.allow_edit === false;
  const downloadUrl =
    typeof action_request?.args?.documento_descarga === "string"
      ? (action_request.args.documento_descarga as string)
      : null;
  // Contexto de origen (texto libre): de dónde vino el documento. Se muestra siempre
  // que venga; es lo único que sitúa al revisor cuando la extracción falla.
  const origen =
    typeof action_request?.args?.origen === "string" && action_request.args.origen.trim()
      ? (action_request.args.origen as string).trim()
      : null;

  // Divisa VIVA de la factura (código ISO): el valor que el revisor ve/edita en el
  // campo `moneda` (fallback al del payload si aún no se inicializó). Etiqueta TODOS
  // los importes de la vista (inputs, cuadre, total) y se actualiza al editarla.
  // Vacía (aprobaciones antiguas sin moneda) -> importes sin sufijo, no asumimos €.
  const monedaViva =
    normalizeCurrency(scalars.moneda) || normalizeCurrency(action_request?.args?.moneda);

  const run = async (response: { type: string; args: unknown }) => {
    setBusy(true);
    setError(null);
    try {
      await onResume(response);
      onResolved();
    } catch (err) {
      const msg = err instanceof ApiError ? `${t("approvals.error")} (${err.status})` : t("approvals.error");
      setError(msg);
      setBusy(false);
    }
  };

  // Reprocesar: cierra esta tarea y el agente vuelve a analizar el documento con
  // el código actual (run nuevo). Dos clics (armar + confirmar) en vez de un
  // window.confirm: mismo flujo visual que el resto de acciones. Solo tiene
  // sentido en tareas nacidas de /runs/async (convención de thread "async-").
  const canReprocess = !!onReprocess && item.thread_id.startsWith("async-");
  const handleReprocess = async () => {
    if (!reprocessArmed) {
      setReprocessArmed(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onReprocess!();
      onResolved();
    } catch (err) {
      const msg = err instanceof ApiError ? `${t("approvals.error")} (${err.status})` : t("approvals.error");
      setError(msg);
      setBusy(false);
      setReprocessArmed(false);
    }
  };

  const buildEditedArgs = (): { changed: boolean; args: Record<string, unknown> } => {
    const newArgs: Record<string, unknown> = {};
    let changed = false;
    for (const [k, v] of Object.entries(action_request.args)) {
      if (PRESENTATION_KEYS.has(k)) newArgs[k] = v;
    }
    for (const k of parsed.scalarKeys) {
      const original = action_request.args[k];
      // Los readonly no se editan: se reenvian TAL CUAL (sin transformar formato ni
      // contar como cambio). Evita que p.ej. una fecha readonly mostrada en otro
      // formato dispare un "edit" fantasma.
      if (readonlyFields.has(k)) {
        newArgs[k] = original;
        continue;
      }
      const next = inputToValue(scalars[k] ?? "", original, NUMERIC_FIELDS.has(k));
      newArgs[k] = next;
      if (JSON.stringify(next) !== JSON.stringify(original)) changed = true;
    }
    for (const k of parsed.arrayKeys) {
      const originalRows = action_request.args[k] as Record<string, unknown>[];
      const cols = parsed.rowKeysByArg[k];
      const nextRows = (rows[k] ?? []).map((cells, rowIdx) => {
        const originalRow = originalRows[rowIdx] ?? {};
        const obj: Record<string, unknown> = { ...originalRow };
        cols.forEach((col, colIdx) => {
          obj[col] = inputToValue(cells[colIdx] ?? "", originalRow[col], NUMERIC_FIELDS.has(col));
        });
        return obj;
      });
      newArgs[k] = nextRows;
      if (JSON.stringify(nextRows) !== JSON.stringify(originalRows)) changed = true;
    }
    return { changed, args: newArgs };
  };

  const handleApprove = () => {
    const { changed, args } = buildEditedArgs();
    // accept SOLO si el revisor no ha tocado nada en toda la sesion. Si edito algo
    // (aunque luego lo dejara igual que el original, p.ej. corrigiendo un valor tras
    // un error), mandamos EDIT con los valores MOSTRADOS: el backend debe registrar
    // lo que el revisor ve, no un propuesta_imputacion de state que un edit fallido
    // anterior pudo dejar pisado. (`changed` compara contra el payload; no basta,
    // porque el state del backend puede divergir del payload mostrado.)
    if (!dirty && !changed && hasAccept) {
      run({ type: "accept", args: action_request });
      return;
    }
    const edited: ActionRequest = { action: action_request.action, args };
    run({ type: "edit", args: edited });
  };

  // Cualquier edicion de un campo escalar marca el formulario como dirty (ver el
  // comentario de `dirty`): garantiza que "Aprobar" mande un edit con el valor MOSTRADO.
  const setScalar = (k: string, v: string) => {
    setDirty(true);
    setScalars((prev) => ({ ...prev, [k]: v }));
    // Al cambiar el DESTINO, el IVA de una linea que ya no es de su pais se vacia:
    // la validacion de obligatorios bloquea Aprobar hasta elegir uno valido. Solo
    // aqui (una eleccion del revisor), nunca al pintar la propuesta inicial.
    if (k === "destino") {
      const permitidos = ivaPorDestino(fieldOptions.vat, fieldOptions.destino, v);
      if (!permitidos) return;
      const validos = new Set(permitidos.map((o) => o.value));
      setRows((prev) => {
        const next = { ...prev };
        for (const arg of parsed.arrayKeys) {
          const vatIdx = parsed.rowKeysByArg[arg].indexOf("vat");
          if (vatIdx < 0 || !next[arg]) continue;
          next[arg] = next[arg].map((cells) =>
            cells[vatIdx] && !validos.has(cells[vatIdx])
              ? cells.map((c, i) => (i === vatIdx ? "" : c))
              : cells,
          );
        }
        return next;
      });
    }
  };

  const setRowCell = (arg: string, rowIdx: number, colIdx: number, value: string) => {
    setDirty(true);
    setRows((prev) => {
      const matrix = (prev[arg] ?? []).map((r) => [...r]);
      if (!matrix[rowIdx]) matrix[rowIdx] = [];
      matrix[rowIdx][colIdx] = value;
      // Regla de negocio: al cambiar el DEPARTAMENTO, tipo_coste y area se derivan de
      // el (deptos 01-14). Los recalculamos EN VIVO aqui para que el revisor vea la
      // combinacion final antes de aprobar. Deptos libres (15/99) no estan en la regla
      // -> no se tocan (el revisor elige). El backend re-aplica la regla al grabar
      // (fuente de verdad), asi que esto es solo feedback inmediato.
      const cols = parsed.rowKeysByArg[arg];
      if (cols?.[colIdx] === "departamento") {
        const regla = deptoRegla.get(normDepto(value));
        if (regla) {
          const tcIdx = cols.indexOf("tipo_coste");
          const areaIdx = cols.indexOf("area");
          if (tcIdx >= 0) matrix[rowIdx][tcIdx] = regla[0];
          if (areaIdx >= 0) matrix[rowIdx][areaIdx] = regla[1];
        }
      }
      return { ...prev, [arg]: matrix };
    });
  };

  // Cuadre del reparto: suma de las BASES de las lineas vs la base de la factura
  // (cabecera). Solo aplica si ambas existen y la cabecera tiene `base`. Aviso NO
  // bloqueante: el revisor manda; solo le advertimos si el reparto no cuadra.
  const reconciliation = (() => {
    const headerBase = parseDisplayedNumber(scalars.base ?? "");
    if (headerBase === null) return null;
    let sum = 0;
    let anyLineBase = false;
    for (const arg of parsed.arrayKeys) {
      const baseCol = parsed.rowKeysByArg[arg].indexOf("base");
      if (baseCol < 0) continue;
      for (const cells of rows[arg] ?? []) {
        const n = parseDisplayedNumber(cells[baseCol] ?? "");
        if (n !== null) {
          sum += n;
          anyLineBase = true;
        }
      }
    }
    if (!anyLineBase) return null;
    // Tolerancia de redondeo (1 centimo) para no avisar por ruido decimal.
    const balanced = Math.abs(sum - headerBase) <= 0.01;
    return { sum, headerBase, balanced };
  })();

  // Total EN VIVO = Σ(base + iva) de las lineas. Se recalcula al editar cualquier
  // importe de linea (a diferencia del `total` de cabecera, que es estatico). Si
  // ninguna linea tiene importes, cae al total de cabecera (scalars.total).
  const computedTotal = (() => {
    let sum = 0;
    let anyLineAmount = false;
    for (const arg of parsed.arrayKeys) {
      const cols = parsed.rowKeysByArg[arg];
      const baseCol = cols.indexOf("base");
      const ivaCol = cols.indexOf("iva");
      for (const cells of rows[arg] ?? []) {
        const b = baseCol >= 0 ? parseDisplayedNumber(cells[baseCol] ?? "") : null;
        const i = ivaCol >= 0 ? parseDisplayedNumber(cells[ivaCol] ?? "") : null;
        if (b !== null) { sum += b; anyLineAmount = true; }
        if (i !== null) { sum += i; anyLineAmount = true; }
      }
    }
    if (!anyLineAmount) return null;  // sin importes de linea -> usar total de cabecera
    // La RETENCION de IRPF se RESTA: el total de una factura de autonomo es el
    // LIQUIDO (base + IVA - retencion), que es lo que se graba en X3 (AMTATILIN) y
    // por lo que nace el vencimiento. Sin restarla aqui el revisor aprueba mirando
    // un importe que NO es el que se va a contabilizar ni el que dice la factura.
    // Llega en POSITIVO desde la extraccion (ver FacturaExtraida.retencion).
    const ret = parseDisplayedNumber(scalars["retencion"] ?? "");
    return ret !== null ? sum - Math.abs(ret) : sum;
  })();

  // --- Validacion de campos editables (defensa en front; el guard del backend la
  // repite). Errores por: importe con texto no numerico, o campo obligatorio vacio.
  // Mientras haya errores, "Aprobar" se deshabilita y los campos se marcan en rojo.
  const scalarErrors: Record<string, string> = {};
  for (const k of parsed.scalarKeys) {
    if (readonlyFields.has(k)) continue;
    const err = validateField(scalars[k] ?? "", {
      numeric: NUMERIC_FIELDS.has(k),
      required: REQUIRED_SCALAR_FIELDS.has(k),
    });
    if (err) scalarErrors[k] = err;
  }
  // Errores de celda de linea, indexados "arg.row.col". `departamento` es obligatorio
  // SALVO que haya clave de reparto (que define la analitica a nivel asiento).
  const tieneClaveReparto = !!(scalars["clave_reparto"] ?? "").trim();
  // Desplegable de IVA de las lineas filtrado por el pais del destino EN VIVO.
  const ivaDelDestino = ivaPorDestino(fieldOptions.vat, fieldOptions.destino, scalars["destino"]);
  const cellErrors: Record<string, string> = {};
  for (const arg of parsed.arrayKeys) {
    const cols = parsed.rowKeysByArg[arg];
    (rows[arg] ?? []).forEach((cells, rowIdx) => {
      cols.forEach((col, colIdx) => {
        const required =
          LINE_REQUIRED_FIELDS.has(col) ||
          (!tieneClaveReparto && LINE_REQUIRED_IF_NO_REPARTO.has(col));
        const err = validateField(cells[colIdx] ?? "", {
          numeric: NUMERIC_FIELDS.has(col),
          required,
        });
        if (err) cellErrors[`${arg}.${rowIdx}.${colIdx}`] = err;
      });
    });
  }
  const hasErrors = Object.keys(scalarErrors).length > 0 || Object.keys(cellErrors).length > 0;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden p-4 sm:p-5">
      {/* Encabezado: titulo + chip de agente + fecha + (toggle documento). */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{prettifyText(item.action)}</h2>
        <span className={`rounded-full px-2 py-0.5 text-xs ${agentChipClass(item.assistant_id)}`}>
          {t(`agents.${item.assistant_id}.label`, item.assistant_id)}
        </span>
        {item.created_at && (
          <span className="ml-auto text-xs text-[var(--muted-foreground)]">
            {t("approvals.created")} {new Date(item.created_at).toLocaleString()}
          </span>
        )}
        {hasDoc && !docPanelOpen && (
          <button
            type="button"
            onClick={() => setDocPanelOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-xs font-medium hover:opacity-90"
          >
            <FileText size={14} />
            {t("approvals.viewDocument")}
          </button>
        )}
      </div>

      {/* Cuerpo: split datos | documento (en pantallas anchas), con divisor
          redimensionable. En movil se apila (gap-4) y el divisor se oculta. */}
      <div ref={splitRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden lg:flex-row lg:gap-0">
        {/* Columna de DATOS editables (scroll propio). Ancho minimo en lg para que
            los campos no se aplasten al arrastrar el divisor; padding lateral para
            que el ring de foco no se recorte ni los campos toquen el divisor. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-1 pt-1 lg:min-w-[22rem] lg:pr-3">
          {/* Sin guia generica fija: el "Resumen del agente" (informe) ya dice de forma
              especifica que revisar. Si no hay informe, los badges de motivos quedan. */}
          {/* Tarjeta de contexto: resumen del agente (informe) + origen del documento.
              Ambos comparten la misma tarjeta (borde izquierdo + fondo muted) para
              integrarse visualmente. El ORIGEN (texto libre, agnóstico al transporte:
              email hoy, GesDoc mañana) se muestra SIEMPRE que venga — sitúa al revisor,
              sobre todo si la extracción falló. Va como pie de la tarjeta, separado por
              una línea sutil, con icono de bandeja de entrada. */}
          {(informe || origen) && (
            <div className="mb-4 shrink-0 overflow-hidden rounded-lg border-l-4 border-[var(--primary)] bg-[var(--muted)]">
              {informe && (
                <div className="px-4 py-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--muted-foreground)]">
                    {t("approvals.report")}
                  </p>
                  <p className="whitespace-pre-line text-sm text-[var(--foreground)]">{informe}</p>
                </div>
              )}
              {origen && (
                <div
                  className={`flex items-start gap-2 px-4 py-2.5 text-xs text-[var(--muted-foreground)]${
                    informe ? " border-t border-[var(--border)]" : ""
                  }`}
                >
                  <Inbox size={14} className="mt-0.5 shrink-0" />
                  <span className="whitespace-pre-line">
                    <span className="font-medium">{t("approvals.origin")}: </span>
                    <span className="text-[var(--foreground)]">{origen}</span>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* En modo solo-rechazar (documento ilegible) NO mostramos badges de motivos:
              no aportan al revisor cuando no hay datos. Solo el resumen y la descarga. */}
          {!soloRechazar && motivos.length > 0 && (
            <div className="mb-4 flex shrink-0 flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-[var(--muted-foreground)]">
                {t("approvals.reasons")}:
              </span>
              {motivos.map((m) => (
                <span
                  key={m}
                  className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                >
                  {t(`approvals.reason.${m.split(":")[0]}`, prettifyText(m.split(":")[0]))}
                </span>
              ))}
            </div>
          )}

          {/* Enlace de DESCARGA del documento original: util sobre todo en solo-rechazar
              (no hay preview porque no renderizo). Se ofrece si hay url de descarga. */}
          {soloRechazar && downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm font-medium hover:opacity-90"
            >
              <FileText size={16} />
              {t("approvals.downloadDocument")}
            </a>
          )}

          {error && (
            <div role="alert" className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Campos editables + cuadre: NO se renderizan en solo-rechazar (documento
              ilegible -> todos vacios, no hay nada que revisar ni editar). */}
          {!soloRechazar && (
          <>
          <div className="space-y-4">
            {parsed.scalarKeys.some((k) => k !== "total") && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {parsed.scalarKeys.filter((k) => k !== "total").map((k) => {
                  const isReadonly = readonlyFields.has(k);
                  // Opciones de cabecera (p.ej. clave_reparto): si el campo escalar trae
                  // opciones en _field_options y NO es readonly, se edita con <select>
                  // (igual que las dimensiones de línea). El value vacío = "destino único".
                  const scalarOptions = fieldOptions[k];
                  const scalarHasOptions = !isReadonly && Array.isArray(scalarOptions) && scalarOptions.length > 0;
                  const scalarValue = scalars[k] ?? "";
                  const scalarSelectOptions: FieldOption[] =
                    scalarHasOptions && scalarValue && !scalarOptions.some((o) => o.value === scalarValue)
                      ? [{ value: scalarValue, label: scalarValue }, ...scalarOptions]
                      : scalarOptions ?? [];
                  return (
                    <div key={k} className={WIDE_FIELDS.has(k) ? "sm:col-span-2" : undefined}>
                      <label className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]">
                        {prettifyText(k)}
                      </label>
                      {isReadonly ? (
                        // Solo lectura: identifica la factura (validado por AEAT), no
                        // editable. Fechas en formato local; importes con la divisa
                        // de la factura (código ISO; sin divisa, solo el número).
                        <p className="w-full rounded-lg border border-transparent bg-[var(--muted)] px-3 py-1.5 text-sm text-[var(--foreground)]">
                          {DATE_FIELDS.has(k)
                            ? (formatDateDisplay(scalars[k] ?? "") || "—")
                            : PERCENT_FIELDS.has(k)
                              ? (scalars[k] ? `${scalars[k]} %` : "—")
                              : NUMERIC_FIELDS.has(k)
                                ? (scalars[k] ? withCurrency(scalars[k], monedaViva) : "—")
                                : (scalars[k] || "—")}
                        </p>
                      ) : scalarHasOptions ? (
                        // Campo de cabecera con catálogo (clave_reparto): <select>. Para
                        // clave_reparto, VACÍO es una opción VÁLIDA (= destino único, lo
                        // más común), NO un hueco por rellenar -> estilo normal (no ámbar)
                        // y NO añadimos placeholder propio si las opciones ya traen una
                        // opción vacía del backend ("— Destino único —"); así no se duplica
                        // ni se pisa con "— Selecciona —". Un campo OBLIGATORIO (destino)
                        // vacío dice que FALTA elegir, igual que los selects de línea.
                        <select
                          aria-label={k}
                          aria-required={REQUIRED_SCALAR_FIELDS.has(k) || undefined}
                          value={scalarValue}
                          onChange={(e) => setScalar(k, e.target.value)}
                          className={fieldClass(!!scalarErrors[k])}
                        >
                          {!scalarValue && !scalarSelectOptions.some((o) => o.value === "") && (
                            <option value="">
                              {REQUIRED_SCALAR_FIELDS.has(k)
                                ? t("approvals.selectRequired")
                                : t("approvals.selectPlaceholder")}
                            </option>
                          )}
                          {scalarSelectOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : DATE_FIELDS.has(k) ? (
                        // Campo de fecha: selector nativo (no caja de texto). El estado
                        // guarda yyyy-MM-dd (ISO), que es lo que X3 acepta.
                        <input
                          type="date"
                          aria-label={k}
                          value={scalars[k] ?? ""}
                          onChange={(e) => setScalar(k, e.target.value)}
                          className={fieldClass(!!scalarErrors[k])}
                        />
                      ) : WIDE_FIELDS.has(k) ? (
                        // Campo ancho de texto libre (observaciones): textarea para ver
                        // el texto completo sin recortar, con el foco bien visible.
                        <textarea
                          aria-label={k}
                          value={scalars[k] ?? ""}
                          onChange={(e) => setScalar(k, e.target.value)}
                          rows={2}
                          className={fieldClass(!!scalarErrors[k], "resize-y")}
                        />
                      ) : NUMERIC_FIELDS.has(k) ? (
                        <MoneyInput
                          ariaLabel={k}
                          value={scalars[k] ?? ""}
                          error={!!scalarErrors[k]}
                          onChange={(v) => setScalar(k, v)}
                          currency={monedaViva}
                        />
                      ) : (
                        <input
                          type="text"
                          aria-label={k}
                          value={scalars[k] ?? ""}
                          onChange={(e) => setScalar(k, e.target.value)}
                          className={fieldClass(!!scalarErrors[k])}
                        />
                      )}
                      {scalarErrors[k] && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                          {t(`approvals.validation.${scalarErrors[k] === "obligatorio" ? "required" : "numeric"}`)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {parsed.arrayKeys.map((k) => {
              const cols = parsed.rowKeysByArg[k];
              const matrix = rows[k] ?? [];
              return (
                <div key={k}>
                  <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-[var(--muted-foreground)]">
                    {prettifyText(k)}
                  </label>
                  <div className="space-y-3">
                    {matrix.map((cells, rowIdx) => (
                      <div key={rowIdx} className="rounded-lg border border-[var(--border)] bg-[var(--muted)] p-3">
                        {matrix.length > 1 && (
                          <div className="mb-2 text-xs font-semibold text-[var(--muted-foreground)]">
                            {t("approvals.lineNumber", { number: rowIdx + 1 })}
                          </div>
                        )}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {cols.map((col, colIdx) => {
                            const isWide = WIDE_FIELDS.has(col);
                            const isNumeric = NUMERIC_FIELDS.has(col);
                            const options = col === "vat" && ivaDelDestino ? ivaDelDestino : fieldOptions[col];
                            const value = cells[colIdx] ?? "";
                            const hasOptions = Array.isArray(options) && options.length > 0;
                            // Si el valor actual (codigo) no esta entre las opciones,
                            // lo anteponemos como opcion propia para no perderlo (con su
                            // etiqueta del catalogo si la tiene: un IVA de otro pais que
                            // el filtro por destino oculta).
                            const selectOptions: FieldOption[] =
                              hasOptions && value && !options.some((o) => o.value === value)
                                ? [fieldOptions[col]?.find((o) => o.value === value) ?? { value, label: value }, ...options]
                                : options ?? [];
                            const onChange = (next: string) => setRowCell(k, rowIdx, colIdx, next);
                            const cellError = cellErrors[`${k}.${rowIdx}.${colIdx}`];
                            // ¿Es un campo obligatorio? (mismo criterio que cellErrors:
                            // siempre los requeridos + departamento si no hay clave de
                            // reparto). Se usa para el asterisco y el placeholder.
                            const colRequired =
                              LINE_REQUIRED_FIELDS.has(col) ||
                              (!tieneClaveReparto && LINE_REQUIRED_IF_NO_REPARTO.has(col));
                            // tipo_coste y area los FIJA el departamento (regla de
                            // negocio): si el depto de ESTA linea tiene regla (01-14),
                            // se muestran READONLY (el revisor no los teclea, se derivan
                            // solos al cambiar el depto). Deptos libres (15/99) no tienen
                            // regla -> se editan con normalidad.
                            const deptoIdx = cols.indexOf("departamento");
                            const deptoVal = deptoIdx >= 0 ? (cells[deptoIdx] ?? "") : "";
                            const lockedByDepto =
                              (col === "tipo_coste" || col === "area") &&
                              deptoRegla.has(normDepto(deptoVal));
                            return (
                              <div key={col} className={isWide ? "sm:col-span-2" : undefined}>
                                <label className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]">
                                  {prettifyText(col)}
                                  {colRequired && !lockedByDepto && <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>}
                                </label>
                                {lockedByDepto ? (
                                  // Derivado del departamento: <select> DESHABILITADO (no
                                  // texto libre). Muestra el mismo desplegable pero bloqueado
                                  // con el valor fijado por la regla. El revisor ve que es un
                                  // campo de lista, solo que no editable (lo fija el depto).
                                  <select
                                    aria-label={`${k}.${rowIdx}.${col}`}
                                    value={value}
                                    disabled
                                    className={`${selectClass(false)} cursor-not-allowed opacity-70`}
                                  >
                                    {!value && <option value="">—</option>}
                                    {selectOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : hasOptions ? (
                                  <select
                                    aria-label={`${k}.${rowIdx}.${col}`}
                                    aria-required={colRequired || undefined}
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className={selectClass(!!cellError)}
                                  >
                                    {/* Placeholder vacio: sin el, un value="" muestra la 1a
                                        opcion (parece elegida sin estarlo). El texto deja claro
                                        que FALTA elegir; el estilo ambar punteado lo refuerza. */}
                                    {!value && (
                                      <option value="">
                                        {colRequired ? t("approvals.selectRequired") : t("approvals.selectEmpty")}
                                      </option>
                                    )}
                                    {selectOptions.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : isWide ? (
                                  <textarea
                                    aria-label={`${k}.${rowIdx}.${col}`}
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    rows={2}
                                    className={fieldClass(!!cellError, "resize-y")}
                                  />
                                ) : isNumeric ? (
                                  <MoneyInput
                                    ariaLabel={`${k}.${rowIdx}.${col}`}
                                    value={value}
                                    error={!!cellError}
                                    onChange={onChange}
                                    currency={monedaViva}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    aria-label={`${k}.${rowIdx}.${col}`}
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className={fieldClass(!!cellError)}
                                  />
                                )}
                                {cellError && (
                                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                    {t(`approvals.validation.${cellError === "obligatorio" ? "required" : "numeric"}`)}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cuadre del reparto: Σ bases de linea vs base de factura. Aviso NO
              bloqueante (se puede aprobar igual); verde si cuadra, ambar si no. */}
          {reconciliation && (
            <div
              className={`mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-3 py-2 text-sm ${
                reconciliation.balanced
                  ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                  : "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
              }`}
            >
              <span className="font-medium">{t("approvals.reconcile.label")}:</span>
              <span>
                {t("approvals.reconcile.sum", { value: formatMoney(reconciliation.sum, monedaViva) })}
              </span>
              {reconciliation.balanced ? (
                <span>{t("approvals.reconcile.ok")}</span>
              ) : (
                <span className="font-medium">
                  {t("approvals.reconcile.mismatch", { value: formatMoney(reconciliation.headerBase, monedaViva) })}
                </span>
              )}
            </div>
          )}

          {/* Importe TOTAL de la factura: se muestra DESPUES de las lineas (no en la
              cabecera), como cierre del bloque de imputacion. Se RECALCULA en vivo como
              Σ(base + iva) de las lineas al editar sus importes; si las lineas no traen
              importes, cae al total de cabecera. */}
          {parsed.scalarKeys.includes("total") && (
            <div className="mt-3 flex items-center justify-between rounded-lg border border-[var(--border)] bg-[var(--muted)] px-3 py-2">
              <span className="text-sm font-medium text-[var(--muted-foreground)]">
                {prettifyText("total")}
              </span>
              <span className="text-base font-semibold text-[var(--foreground)]">
                {computedTotal !== null
                  ? formatMoney(computedTotal, monedaViva)
                  : (scalars.total ? withCurrency(scalars.total, monedaViva) : "—")}
              </span>
            </div>
          )}
          </>
          )}

          {/* Acciones, pegadas al fondo de la columna (sticky). z-10 para quedar por
              encima del contenido que scrollea; fondo opaco a todo el ancho (-mx-1 px-1
              compensa el padding lateral del contenedor); pb-2 para no tocar el borde.
              El contenedor padre NO tiene padding inferior (pt-1), asi la banda se pega
              al fondo real y nada asoma por debajo. */}
          <div className="sticky bottom-0 z-10 -mx-1 mt-5 flex flex-wrap items-center gap-2 border-t border-[var(--border)] bg-[var(--background)] px-1 pb-2 pt-4">
            {config.allow_accept && (
              <button
                onClick={handleApprove}
                disabled={busy || hasErrors}
                title={hasErrors ? t("approvals.validation.fix") : undefined}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {t("approvals.action.accept")}
              </button>
            )}
            {!soloRechazar && hasErrors && (
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                {t("approvals.validation.fix")}
              </span>
            )}
            {config.allow_ignore && (
              <button
                onClick={() => run({ type: "ignore", args: null })}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
              >
                <X size={16} />
                {t("approvals.action.ignore")}
              </button>
            )}
            {canReprocess && (
              <button
                onClick={handleReprocess}
                disabled={busy}
                className="ml-auto flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--muted-foreground)] hover:bg-[var(--muted)] disabled:opacity-50"
              >
                <RefreshCw size={16} />
                {reprocessArmed
                  ? t("approvals.action.reprocessConfirm")
                  : t("approvals.action.reprocess")}
              </button>
            )}
          </div>
        </div>

        {/* Divisor arrastrable (solo en split horizontal, lg+). */}
        {hasDoc && docPanelOpen && <DocumentDivider onPointerDown={onDividerPointerDown} />}

        {/* Columna del DOCUMENTO (split). Ancho fijo en lg (controlado por el
            divisor); ancho completo apilado en movil. min-w-0 para que su contenido
            nunca fuerce desbordamiento contra el margen del contenedor. */}
        {hasDoc && docPanelOpen && (
          <div
            className="min-h-0 w-full min-w-0 shrink-0 lg:w-[var(--doc-w)]"
            style={{ ["--doc-w" as string]: `${docWidthPct}%` }}
          >
            <DocumentPanel
              urls={documentUrls}
              page={page}
              setPage={setPage}
              onZoom={() => setModalOpen(true)}
              onClose={() => setDocPanelOpen(false)}
              downloadUrl={downloadUrl}
            />
          </div>
        )}
      </div>

      {modalOpen && hasDoc && (
        <DocumentModal urls={documentUrls} page={page} setPage={setPage} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}
